import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Container } from "reactstrap";
import Breadcrumbs from "src/components/Common/Breadcrumb";
import RichTable, { RichColumn } from "src/components/Page/RichTable";
import ColumnButton from "src/components/Page/GridButtons/ColumnButton";
import { useAppDispatch } from "src/store";
import { getOnboardingQueue } from "src/store/client-onboarding/saga";
import {
  ONBOARDING_STATUS_COLOR,
  ONBOARDING_STATUS_LABEL,
  OnboardingQueueItem,
  OnboardingQueueResponse,
  RISK_LANE_COLOR,
} from "src/store/client-onboarding/type";
import { GetLocaleDate } from "src/helpers/getLocaleDate";
import OnboardingReviewDrawer from "./review";

/**
 * Tedarikçi onboarding kuyruğu — V2 Faz E.
 *
 * Onboarding kararı yalnız burada verilir. Rentificial Ai'daki ayrı portal ve
 * admin-panel ekranları kaldırıldı; çift onay noktası bırakılmadı.
 */
const ClientOnboardingPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [data, setData] = useState<OnboardingQueueItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<number | "">("");
  const [search, setSearch] = useState("");
  const [reviewId, setReviewId] = useState<string>();

  document.title = t("ClientOnboarding.MetaTag", "Tedarikçi Onboarding") as string;

  const fetchData = () => {
    setLoading(true);
    const params =
      "?skip=0&take=200" +
      (statusFilter === "" ? "" : `&status=${statusFilter}`) +
      (search.trim() ? `&search=${encodeURIComponent(search.trim())}` : "");
    dispatch(
      getOnboardingQueue({
        params,
        payload: {
          onSuccess: (_m: any, p: OnboardingQueueResponse) => {
            setData(p?.Items ?? []);
            setTotal(p?.Total ?? 0);
            setLoading(false);
          },
          onError: () => setLoading(false),
          onFinally: () => setLoading(false),
        },
      })
    );
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const columns: RichColumn<OnboardingQueueItem>[] = [
    { key: "ClientName", label: t("ClientOnboarding.Client", "Firma") },
    { key: "SectorName", label: t("ClientOnboarding.Sector", "Sektör") },
    {
      key: "OnboardingStatusId",
      label: t("ClientOnboarding.Status", "Durum"),
      render: (r) => (
        <span className={`badge bg-${ONBOARDING_STATUS_COLOR[r.OnboardingStatusId] ?? "secondary"}`}>
          {ONBOARDING_STATUS_LABEL[r.OnboardingStatusId] ?? r.OnboardingStatus}
        </span>
      ),
    },
    {
      key: "RiskLane",
      label: t("ClientOnboarding.Risk", "Risk"),
      render: (r) =>
        r.RiskLane ? (
          <span className={`badge bg-${RISK_LANE_COLOR[r.RiskLane] ?? "secondary"}`}>
            {r.RiskScore ?? "?"} · {r.RiskLane}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: "ApprovedDocCount",
      label: t("ClientOnboarding.Documents", "Belge"),
      render: (r) => (
        <span className={r.DocCount > 0 && r.ApprovedDocCount === r.DocCount ? "text-success" : ""}>
          {r.ApprovedDocCount} / {r.DocCount}
        </span>
      ),
    },
    { key: "Email", label: t("ClientOnboarding.Email", "E-posta") },
    {
      key: "SubmittedAt",
      label: t("ClientOnboarding.SubmittedAt", "Başvuru"),
      render: (r) => (r.SubmittedAt ? GetLocaleDate(r.SubmittedAt) : "—"),
    },
  ];

  /** Kuyruk sekmeleri — operatörün gününü bu sıra belirler. */
  const tabs: Array<{ id: number | ""; label: string }> = [
    { id: "", label: t("ClientOnboarding.TabAll", "Tümü") as string },
    { id: 3, label: t("ClientOnboarding.TabReview", "İncelemede") as string },
    { id: 2, label: t("ClientOnboarding.TabDocs", "Belge Bekleyen") as string },
    { id: 5, label: t("ClientOnboarding.TabYellow", "Sarı") as string },
    { id: 6, label: t("ClientOnboarding.TabRed", "Reddedilen") as string },
    { id: 7, label: t("ClientOnboarding.TabVerified", "Doğrulanmış") as string },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex flex-wrap align-items-center justify-content-between">
            <Breadcrumbs
              title={t("Breadcrumb.Dashboard")}
              breadcrumbItem={t("ClientOnboarding.PageTitle", "Tedarikçi Onboarding") as string}
            />
            <div className="d-flex gap-2 mb-3">
              <input
                className="form-control"
                style={{ minWidth: 220 }}
                placeholder={t("ClientOnboarding.SearchPlaceholder", "Firma, e-posta, vergi no") as string}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchData()}
              />
              <button className="btn btn-warning" onClick={fetchData} title={t("Components.Refresh", "Yenile") as string}>
                <i className="mdi mdi-refresh" />
              </button>
            </div>
          </div>

          <ul className="nav nav-pills mb-3 gap-1">
            {tabs.map((tab) => (
              <li className="nav-item" key={String(tab.id)}>
                <button
                  type="button"
                  className={`nav-link ${statusFilter === tab.id ? "active" : ""}`}
                  onClick={() => setStatusFilter(tab.id)}
                >
                  {tab.label}
                </button>
              </li>
            ))}
            <li className="nav-item ms-auto d-flex align-items-center">
              <span className="text-muted">
                {t("ClientOnboarding.Total", "Toplam")}: <strong>{total}</strong>
              </span>
            </li>
          </ul>

          <RichTable<OnboardingQueueItem>
            tableId="client-onboardings"
            t={t as any}
            columns={columns}
            rows={data}
            loading={loading}
            rowKey={(r) => r.Id}
            emptyText={t("ClientOnboarding.Empty", "Bu kuyrukta başvuru yok") as string}
            actions={(r) => (
              <>
                <ColumnButton buttonType="details" onClick={() => setReviewId(r.Id)} />
                <ColumnButton buttonType="edit" onClick={() => navigate("/clients/details/1/" + r.Id)} />
              </>
            )}
          />
        </Container>
      </div>

      {reviewId && (
        <OnboardingReviewDrawer
          clientId={reviewId}
          open={!!reviewId}
          onHide={() => setReviewId(undefined)}
          onChanged={() => {
            fetchData();
          }}
        />
      )}
    </React.Fragment>
  );
};

export default ClientOnboardingPage;
